/**
* Mobile Navigation Drawer
*/
class MobileNavigation extends HTMLElement {
    constructor() {
      super();
      
      this.touchstartX = 0;
      this.touchendX = 0;
  
      this.openeBy = document.querySelector('#mobile-menu');
      this.closeBy = document.querySelector('.close-mobile--navbar');
      this.navDrawer = this.querySelector('#mobile-menu-drawer');
      this.levelStack = [];
      this.stickyBackResets = new Map();
      this.bindEvents();
      this.bindLiveChat();
      this.bindLevels();
      this.bindStickyBack();
    }

    /**
     * Nav redesign Oct 2026 (W7): drawer sub-levels (Wristbands → material, Shop By Use, Resources).
     * Rows are buttons (data-drawer-level-toggle, aria-controls = panel id); panels are the existing drawer
     * sub-panels (li.dropdown > .dropdown-menu), shown with the same `open` class and fade as before.
     * Opening a level focuses its back button and makes everything behind it inert; back (or Escape)
     * closes the deepest open level and returns focus to the row that opened it.
     */
    bindLevels() {
      this.querySelectorAll('[data-drawer-level-toggle]').forEach((toggle) => {
        toggle.addEventListener('click', (event) => {
          event.preventDefault();
          this.openLevel(toggle);
        });
      });
      this.querySelectorAll('[data-drawer-level-back]').forEach((back) => {
        back.addEventListener('click', (event) => {
          event.preventDefault();
          this.closeLevel(back.closest('[data-drawer-level]'));
        });
      });
    }

    /**
     * Material screens (L3) only: the back row is sticky (CSS) and compacts (.is-compact) while scrolling down,
     * returning to full size on scroll up. Passive, rAF-throttled listener on the level itself (its own scroll
     * container). Never compacts in the first 40px; after that the state only flips once the scroll has moved
     * 20px in one direction since the last turn. The bar's own 28px height change can clamp scrollTop near the
     * bottom (more than the 20px threshold), so for 200ms after each change (150ms transition + margin) the anchor
     * just follows the scroll and the state can't flip. Only classes change, so the accessible name stays the same.
     * The sticky white bar around the button gets .is-stuck (1px divider) as soon as the level scrolls.
     */
    bindStickyBack() {
      const COMPACT_AFTER = 40;
      const DIRECTION_THRESHOLD = 20;
      const SETTLE_MS = 200;
      this.querySelectorAll('[data-drawer-level].mobile-nav-level--material').forEach((panel) => {
        const back = panel.querySelector('[data-drawer-level-back]');
        if (!back) return;
        const bar = back.closest('.mobile-nav-level-back-bar');
        let compact = false;
        let anchor = 0;
        let ticking = false;
        let settleUntil = 0;
        let settleTimer = null;

        const setCompact = (value) => {
          compact = value;
          back.classList.toggle('is-compact', value);
          if (bar) bar.classList.toggle('is-compact', value);
          settleUntil = performance.now() + SETTLE_MS;
          /* Re-check once settled, in case scrolling stopped inside the window (e.g. back at the top) */
          clearTimeout(settleTimer);
          settleTimer = setTimeout(requestUpdate, SETTLE_MS + 16);
        };

        const update = () => {
          ticking = false;
          const y = panel.scrollTop;
          if (bar) bar.classList.toggle('is-stuck', y > 0);
          /* Resize still animating: scroll changes may be our own height change, not the user */
          if (performance.now() < settleUntil) {
            anchor = y;
            return;
          }
          if (y <= COMPACT_AFTER) {
            if (compact) setCompact(false);
            anchor = y;
            return;
          }
          if (compact) {
            /* Anchor tracks the furthest point down; expand after 20px back up */
            anchor = Math.max(anchor, y);
            if (anchor - y >= DIRECTION_THRESHOLD) {
              setCompact(false);
              anchor = y;
            }
          } else {
            /* Anchor tracks the furthest point up; compact after 20px down */
            anchor = Math.min(anchor, y);
            if (y - anchor >= DIRECTION_THRESHOLD) {
              setCompact(true);
              anchor = y;
            }
          }
        };

        const requestUpdate = () => {
          if (ticking) return;
          ticking = true;
          requestAnimationFrame(update);
        };

        panel.addEventListener('scroll', requestUpdate, { passive: true });

        this.stickyBackResets.set(panel, () => {
          setCompact(false);
          settleUntil = 0;
          clearTimeout(settleTimer);
          if (bar) bar.classList.remove('is-stuck');
          anchor = 0;
        });
      });
    }

    openLevel(toggle) {
      const panel = document.getElementById(toggle.getAttribute('aria-controls'));
      if (!panel || panel.classList.contains('open')) return;
      toggle.parentElement.classList.add('open');
      panel.classList.add('open');
      toggle.setAttribute('aria-expanded', 'true');
      panel.scrollTop = 0;
      this.levelStack.push({ panel, toggle, inerted: this.inertBehind(panel) });
      const back = panel.querySelector('[data-drawer-level-back]');
      if (back) back.focus();
    }

    /**
     * Makes everything outside `panel` (up to the drawer) inert, including the row that opened it.
     * Returns the elements it changed so closing the level can restore exactly those.
     */
    inertBehind(panel) {
      const inerted = [];
      let node = panel;
      while (node && node !== this.navDrawer && node.parentElement) {
        Array.from(node.parentElement.children).forEach((sibling) => {
          if (sibling !== node && !sibling.inert) {
            sibling.inert = true;
            inerted.push(sibling);
          }
        });
        node = node.parentElement;
      }
      return inerted;
    }

    /**
     * Closes open levels from the deepest up to and including `panel`.
     *
     * @param {element} panel - Level to close
     * @param {boolean} restoreFocus - Return focus to the row that opened `panel`
     */
    closeLevel(panel, restoreFocus = true) {
      while (this.levelStack.length) {
        const level = this.levelStack.pop();
        level.inerted.forEach((element) => { element.inert = false; });
        level.panel.classList.remove('open');
        /* Material screens: next visit starts with the full-size back row (openLevel scrolls to the top) */
        const resetStickyBack = this.stickyBackResets.get(level.panel);
        if (resetStickyBack) resetStickyBack();
        level.toggle.parentElement.classList.remove('open');
        level.toggle.setAttribute('aria-expanded', 'false');
        if (level.panel === panel) {
          if (restoreFocus) level.toggle.focus();
          break;
        }
      }
    }

    closeAllLevels() {
      if (this.levelStack.length) this.closeLevel(this.levelStack[0].panel, false);
    }

    /**
     * Nav redesign Oct 2026: Live Chat help tile opens the Gorgias chat widget.
     * The tile is rendered hidden and only shown once Gorgias is ready, so it never does nothing on tap
     * (e.g. if the app embed is disabled or blocked).
     */
    bindLiveChat() {
      /* W7: every drawer level has its own help footer, so bind every Live Chat tile */
      const chatButtons = this.querySelectorAll('[data-open-live-chat]');
      if (!chatButtons.length) return;

      const whenChatReady = (callback) => {
        if (window.GorgiasChat) {
          window.GorgiasChat.init().then(callback);
        } else {
          window.addEventListener('gorgias-widget-loaded', () => {
            window.GorgiasChat.init().then(callback);
          }, { once: true });
        }
      };

      whenChatReady(() => {
        chatButtons.forEach((chatButton) => { chatButton.hidden = false; });
      });

      chatButtons.forEach((chatButton) => {
        chatButton.addEventListener('click', (event) => {
          event.preventDefault();
          if (!window.GorgiasChat) return;
          this.closeBy.dispatchEvent(new Event('click'));
          window.GorgiasChat.open();
        });
      });
    }
  
    /**
     * Focus Out Event to close drawer
     *
     * @param {event} Event instance
     */
    focusOut(event){
      let isOpen = this.navDrawer.classList.contains('opened-drawer');
      if (!this.contains(event.relatedTarget) && isOpen == true) {
        this.closeBy.dispatchEvent(new Event('click'));
      }
    }
  
    /**
     * Escape Key Press to close drawer when focus is within the container
     *
     * @param {event} Event instance
     */
    onKeyUp(event) {
      if(event.code && event.code.toUpperCase() !== 'ESCAPE') return;
      /* W7: Escape steps back one level first; at the top level it closes the drawer */
      if (this.levelStack.length) {
        this.closeLevel(this.levelStack[this.levelStack.length - 1].panel);
        return;
      }
      this.closeBy.dispatchEvent(new Event('click'));
    }
  
    /**
     * bind click event to Menu Icon 
     * bind keyup event to Menu Drawer component
     */
    bindEvents() {
      this.openeBy.addEventListener('click', this.openMenuDrawer.bind(this));
      this.closeBy.addEventListener('click', this.closeMenuDrawer.bind(this));
      this.addEventListener('keyup', this.onKeyUp.bind(this));
      this.navDrawer.addEventListener('click', this.onBackdropClick.bind(this));
    }

    /**
     * Nav redesign Oct 2026: close the drawer when the dim backdrop (drawer ::before, 768px and up) is tapped.
     * Backdrop clicks target the drawer itself but land outside its 390px box.
     *
     * @param {event} Event instance
     */
    onBackdropClick(event) {
      if (event.target !== this.navDrawer) return;
      if (event.clientX <= this.navDrawer.getBoundingClientRect().right) return;
      this.closeBy.dispatchEvent(new Event('click'));
    }
  
    /**
     * Open Menu Drawer
     *
     * @param {event} Event instance
     */
    openMenuDrawer(event) {
      this.openeBy.closest('div').classList.add('menu-opended');
      this.navDrawer.classList.add('opened-drawer');
      document.querySelector('body').classList.add('scroll-fixed');
      document.body.style.overflowY = "hidden";
      /* W8a: place the drawer under the header as it is now (scroll lock applied above) */
      updateHeaderBottom();
      if(document.querySelector('search-modal').classList.contains('open__modal')){
        document.querySelector('.search-close').dispatchEvent(new Event('click'));
      }
      let cartForm = document.querySelector('cart-form.cart-drawer');
      if(cartForm && cartForm.classList.contains('opened-drawer')){
        document.querySelector('.close-ajax--cart').dispatchEvent(new Event('click'));
      }
      if(event){
        event.preventDefault();
        let openBy = event.currentTarget;
        openBy.setAttribute('aria-expanded', true);
      }
    }
  
    /**
     * Close Menu Drawer
     *
     * @param {event} Event instance
     */
    closeMenuDrawer(event, elementToFocus = false) {
      if (event !== undefined) {
        event.preventDefault();
        this.openeBy.closest('div').classList.remove('menu-opended');
        this.navDrawer.classList.remove('opened-drawer');
        document.querySelector('body').classList.remove('scroll-fixed');
        document.body.style.overflowY = "auto";

        /* W7: close drawer sub-levels (restores inert and aria-expanded) */
        this.closeAllLevels();
        let openDropdowns = Array.from(document.querySelectorAll('#mobile-menu-drawer .dropdown.open'));
        openDropdowns.forEach((ele) => {
          ele.classList.remove('open');
          /* W7: drawer levels have no [is="drop-down"]; guard the lookup */
          const toggle = ele.querySelector('[is="drop-down"]');
          if (toggle) toggle.setAttribute('aria-expanded', false);
        });
  
        this.openeBy.setAttribute('aria-expanded', false);
        Utility.forceFocus(this.openeBy);
      }
    }
  
    /**
     * To get User Touch action pattern (Swipe Left / Swipe Right)
     *
     */
    handleGesture() {
      if (this.touchendX < this.thistouchstartX)
        this.openeBy.dispatchEvent(new Event('click'));
      if (this.touchendX > this.touchstartX)
        this.closeBy.dispatchEvent(new Event('click'));
    }
}
customElements.define("mobile-nav", MobileNavigation);